import React, { useState } from 'react';
import { useData } from '../App';
import { AppView } from '../types';
import { MessageCircle, Search, ChevronDown, Quote, ArrowRight, ExternalLink } from 'lucide-react';

interface CommunityFAQProps {
  setView: (view: AppView) => void;
}

const CommunityFAQ: React.FC<CommunityFAQProps> = ({ setView }) => {
  const { faqs } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [openQuestions, setOpenQuestions] = useState<string[]>([]);

  const toggleQuestion = (question: string) => {
    if (openQuestions.includes(question)) {
      setOpenQuestions(openQuestions.filter(q => q !== question));
    } else {
      setOpenQuestions([...openQuestions, question]);
    }
  };

  const categories = ['all', ...faqs.categories.map(c => c.id)];

  const filteredFAQs = faqs.categories
    .filter(cat => activeCategory === 'all' || cat.id === activeCategory)
    .map(cat => ({
      ...cat,
      faqs: cat.faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }))
    .filter(cat => cat.faqs.length > 0);

  // Helper to determine contextual action
  const getContextualAction = (text: string) => {
    const t = text.toLowerCase();
    
    if (t.includes('school') || t.includes('ib') || t.includes('cbse') || t.includes('curriculum')) {
      return { label: 'Compare Schools', view: AppView.SCHOOLS };
    }
    if (t.includes('neighborhood') || t.includes('rent') || t.includes('buy') || t.includes('kharadi') || t.includes('baner')) {
      return { label: 'Explore Neighborhoods', view: AppView.NEIGHBORHOODS };
    }
    if (t.includes('tax') || t.includes('401k') || t.includes('rnor') || t.includes('bank') || t.includes('invest')) {
      return { label: 'View Financial Tools', view: AppView.INVESTMENTS };
    }
    if (t.includes('oci') || t.includes('shipping') || t.includes('checklist') || t.includes('document')) {
      return { label: 'Go to Checklist', view: AppView.CHECKLIST };
    }
    
    return null;
  };

  return (
    <div className="p-6 max-w-4xl mx-auto pb-24 md:pb-6">
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-slate-800 mb-2 flex items-center justify-center gap-3">
          Community Q&A
          <span className="text-xs font-medium px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">🇮🇳 All NRIs</span>
        </h2>
        <p className="text-slate-500">
          Wisdom from hundreds of NRIs who have already made the move.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-8 space-y-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search questions (e.g., 'OCI', 'schools', '401k')"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
          />
          <Search className="absolute left-3 top-3.5 text-slate-400" size={20} />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition
                ${activeCategory === cat 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
            >
              {cat === 'all' ? 'All Topics' : faqs.categories.find(c => c.id === cat)?.name}
            </button>
          ))}
        </div>
      </div>

      {/* FAQs List */}
      <div className="space-y-6">
        {filteredFAQs.map(category => (
          <div key={category.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
               <MessageCircle size={18} className="text-indigo-500" />
               <h3 className="font-bold text-slate-700">{category.name}</h3>
             </div>
             
             <div className="divide-y divide-slate-100">
               {category.faqs.map((faq, idx) => {
                 const isOpen = openQuestions.includes(faq.question);
                 const action = getContextualAction(faq.question + " " + faq.answer);

                 return (
                   <div key={idx} className="transition hover:bg-slate-50/50">
                     <button 
                       onClick={() => toggleQuestion(faq.question)}
                       className="w-full px-6 py-4 text-left flex justify-between items-start gap-4 focus:outline-none"
                     >
                       <span className="font-semibold text-slate-800 text-base md:text-lg">{faq.question}</span>
                       <div className={`mt-1 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                         <ChevronDown size={20} />
                       </div>
                     </button>
                     
                     {isOpen && (
                       <div className="px-6 pb-6 animate-fadeIn">
                         <div className="text-slate-600 leading-relaxed mb-4">
                           {faq.answer}
                         </div>
                         
                         {/* Contextual Action Button */}
                         {action && (
                           <button 
                             onClick={() => setView(action.view)}
                             className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-md hover:bg-indigo-100 transition"
                           >
                             {action.label} <ArrowRight size={12} />
                           </button>
                         )}
                         
                         {faq.chatQuote && (
                           <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100 relative mt-2">
                             <Quote size={20} className="text-indigo-300 absolute top-2 left-2 opacity-50" />
                             <p className="text-indigo-900 italic text-sm pl-6 relative z-10 font-medium">
                               "{faq.chatQuote}"
                             </p>
                             <p className="text-[10px] text-indigo-400 text-right mt-2 uppercase tracking-wide font-bold">
                               Community Consensus
                             </p>
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                 );
               })}
             </div>
          </div>
        ))}
        
        {filteredFAQs.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No questions found matching "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityFAQ;